//src/api/pharmacyApi.ts
import {
  PharmacyCompanySerializer,
  PharmacyAcupunctureSerializer,
  PharmacyType,
  PharmacyDecoctionInfoSerializer,
  PharmacyRemoteDecoctionSearchItem,
} from "@/types/pharmacy"
import { CommonTResponse, CommonResponse } from "@/types/common"
import { handleApiResponse } from "@core/api/core/utils"
import { serverApi, getAuthHeaders, queApi } from "@core/api/core"

export const fetchPharmacyCompanies = async (): Promise<PharmacyCompanySerializer[]> => {
  const requestUrl = `/pharmacy/pharmacy_company/`
  const response = await queApi.get<CommonTResponse<PharmacyCompanySerializer>>(requestUrl)
  return handleApiResponse<PharmacyCompanySerializer[]>(response)
}

export const fetchPharmacyCompaniesWithType = async (type: PharmacyType, isUse?: boolean): Promise<PharmacyCompanySerializer[]> => {
  const requestUrl = `/pharmacy/pharmacy_company/`
  const params: Record<string, any> = { type }

  if (isUse !== undefined) {
    params.is_use = isUse
  }

  const response = await queApi.get<CommonTResponse<PharmacyCompanySerializer>>(requestUrl, { params })

  return handleApiResponse<PharmacyCompanySerializer[]>(response)
}

export const fetchPharmacyAcupunctureList = async (pharmacyCompanyUuid: string, isUse?: boolean): Promise<PharmacyAcupunctureSerializer[]> => {
  const requestUrl = `/pharmacy/pharmacy_acupuncture/`
  const params: Record<string, any> = { pharmacy_company_uuid: pharmacyCompanyUuid }

  if (isUse !== undefined) {
    params.is_use = isUse
  }

  const response = await queApi.get<CommonTResponse<PharmacyAcupunctureSerializer>>(requestUrl, { params })
  return handleApiResponse<PharmacyAcupunctureSerializer[]>(response)
}

export const createPharmacyCompany = async (
  payload: Omit<PharmacyCompanySerializer, "pharmacyCompanyUuid" | "pharmacyAcupunctureCount" | "pharmacyAcupunctureList">
): Promise<PharmacyCompanySerializer> => {
  const requestUrl = `/pharmacy/pharmacy_company/`
  console.info("[pharmacyApi] createPharmacyCompany request", { requestUrl, payload })
  try {
    const response = await serverApi.post<CommonTResponse<PharmacyCompanySerializer>>(requestUrl, payload)
    console.info("[pharmacyApi] createPharmacyCompany response", { requestUrl, data: response?.data })
    return handleApiResponse<PharmacyCompanySerializer>(response)
  } catch (error: any) {
    console.error("[pharmacyApi] createPharmacyCompany error", {
      requestUrl,
      payload,
      status: error?.response?.status,
      data: error?.response?.data,
    })
    throw error
  }
}

export const updatePharmacyCompany = async (payload: Partial<PharmacyCompanySerializer>): Promise<PharmacyCompanySerializer> => {
  const requestUrl = `/pharmacy/pharmacy_company/${payload.pharmacyCompanyUuid}/`
  console.info("[pharmacyApi] updatePharmacyCompany request", { requestUrl, payload })
  const response = await serverApi.patch<CommonTResponse<PharmacyCompanySerializer>>(requestUrl, payload)
  console.info("[pharmacyApi] updatePharmacyCompany response", { requestUrl, data: response?.data })
  return handleApiResponse<PharmacyCompanySerializer>(response)
}

export const deletePharmacyCompany = async (pharmacyCompanyUuid: string): Promise<boolean> => {
  const requestUrl = `/pharmacy/pharmacy_company/${pharmacyCompanyUuid}/`
  const response = await serverApi.delete<CommonTResponse<boolean>>(requestUrl)
  return handleApiResponse<boolean>(response)
}

export const createPharmacyAcupuncture = async (
  payload: Omit<PharmacyAcupunctureSerializer, "pharmacyAcupunctureUuid">
): Promise<PharmacyAcupunctureSerializer> => {
  const requestUrl = `/pharmacy/pharmacy_acupuncture/`
  const response = await serverApi.post<CommonTResponse<PharmacyAcupunctureSerializer>>(requestUrl, payload)
  return handleApiResponse<PharmacyAcupunctureSerializer>(response)
}

export const updatePharmacyAcupuncture = async (payload: Partial<PharmacyAcupunctureSerializer>): Promise<PharmacyAcupunctureSerializer> => {
  const requestUrl = `/pharmacy/pharmacy_acupuncture/${payload.pharmacyAcupunctureUuid}/`
  const response = await serverApi.patch<CommonTResponse<PharmacyAcupunctureSerializer>>(requestUrl, payload)
  return handleApiResponse<PharmacyAcupunctureSerializer>(response)
}

export const deletePharmacyAcupuncture = async (pharmacyAcupunctureUuid: string): Promise<boolean> => {
  const requestUrl = `/pharmacy/pharmacy_acupuncture/${pharmacyAcupunctureUuid}/`
  const response = await serverApi.delete<CommonTResponse<boolean>>(requestUrl)
  return handleApiResponse<boolean>(response)
}

export const fetchExistPharmacyList = async (pharmacyType: PharmacyType): Promise<PharmacyCompanySerializer[]> => {
  const requestUrl = `/pharmacy/pharmacy_company/?type=${pharmacyType}`
  const response = await queApi.get<CommonTResponse<PharmacyCompanySerializer[]>>(requestUrl)
  return handleApiResponse<PharmacyCompanySerializer[]>(response)
}

export const fetchPharmacyDecoctionInfoList = async (pharmacyCompanyUuid?: string, isUse?: boolean): Promise<PharmacyDecoctionInfoSerializer[]> => {
  const requestUrl = `/pharmacy/pharmacy_decoction_info/`
  const params: Record<string, any> = {}

  if (pharmacyCompanyUuid) {
    params.pharmacy_company_uuid = pharmacyCompanyUuid
  }

  if (isUse !== undefined) {
    params.is_use = isUse
  }

  const response = await queApi.get<CommonTResponse<PharmacyDecoctionInfoSerializer[]>>(requestUrl, { params })
  return handleApiResponse<PharmacyDecoctionInfoSerializer[]>(response)
}

export const createPharmacyDecoctionInfo = async (
  payload: Omit<PharmacyDecoctionInfoSerializer, "pharmacyDecoctionInfoUuid" | "pharmacyCompanyName" | "decoctionClassificationDisplay">
): Promise<PharmacyDecoctionInfoSerializer> => {
  const requestUrl = `/pharmacy/pharmacy_decoction_info/`
  console.info("[pharmacyApi] createPharmacyDecoctionInfo request", { requestUrl, payload })
  try {
    const response = await serverApi.post<CommonTResponse<PharmacyDecoctionInfoSerializer>>(requestUrl, payload)
    console.info("[pharmacyApi] createPharmacyDecoctionInfo response", { requestUrl, data: response?.data })
    return handleApiResponse<PharmacyDecoctionInfoSerializer>(response)
  } catch (error: any) {
    console.error("[pharmacyApi] createPharmacyDecoctionInfo error", {
      requestUrl,
      payload,
      status: error?.response?.status,
      data: error?.response?.data,
    })
    throw error
  }
}

export const updatePharmacyDecoctionInfo = async (payload: Partial<PharmacyDecoctionInfoSerializer>): Promise<PharmacyDecoctionInfoSerializer> => {
  const requestUrl = `/pharmacy/pharmacy_decoction_info/${payload.pharmacyDecoctionInfoUuid}/`
  console.info("[pharmacyApi] updatePharmacyDecoctionInfo request", { requestUrl, payload })
  const response = await serverApi.patch<CommonTResponse<PharmacyDecoctionInfoSerializer>>(requestUrl, payload)
  console.info("[pharmacyApi] updatePharmacyDecoctionInfo response", { requestUrl, data: response?.data })
  return handleApiResponse<PharmacyDecoctionInfoSerializer>(response)
}

const REMOTE_DECOCTION_DUMMY: PharmacyRemoteDecoctionSearchItem[] = [
  {
    pharmacyCompanyName: "옥천당서울탕전실",
    pharmacyCompanyCode: "3844502",
    decoctionClassification: 2,
    decoctionRegion: "서울",
    decoctionSerialNumber: "2",
    decoctionPhone: "031-905-0042",
    decoctionAddress: "경기도 고양시 덕양구 호수로 233",
    decoctionLicenseType: 5,
    decoctionLicenseNumber: "1479",
    decoctionLicenseOwnerName: "최미성",
    decoctionHospitalCode: "38445020",
  },
]

export const searchRemoteDecoctionInfoDummy = async (keyword: string): Promise<PharmacyRemoteDecoctionSearchItem[]> => {
  const normalizedKeyword = keyword.trim().toLowerCase()
  if (!normalizedKeyword) {
    return []
  }

  const filtered = REMOTE_DECOCTION_DUMMY.filter(item => {
    const target =
      `${item.pharmacyCompanyName} ${item.pharmacyCompanyCode} ${item.decoctionRegion} ${item.decoctionHospitalCode} ${item.decoctionPhone} ${item.decoctionAddress} ${item.decoctionLicenseOwnerName}`.toLowerCase()
    return target.includes(normalizedKeyword)
  })

  return filtered
}
